import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, BoltIcon } from '@heroicons/react/24/solid';
import { newsflashAPI, NewsFlashQuestion, NewsFlashSessionSummary } from '../../services/api';

type AgeGroup = '6-9' | '10-13' | '14+';
type Phase = 'intro' | 'loading' | 'playing' | 'finished' | 'error' | 'empty';

// Per-age-group game name (Set A)
export const GAME_NAMES: Record<AgeGroup, string> = {
  '6-9': 'Brainy Bolt',
  '10-13': 'Daily Dash',
  '14+': 'Mind Sprint',
};

interface NewsFlashGameProps {
  ageGroup: AgeGroup;
  onClose: () => void;
}

const GAME_SECONDS = 99;
const POINTS_PER_CORRECT = 10;
const FEEDBACK_MS = 800;

// Speed bonus: faster answers earn up to 2 extra points per question.
const speedBonus = (secondsTaken: number): number => {
  if (secondsTaken <= 3) return 2;
  if (secondsTaken <= 6) return 1;
  return 0;
};

interface AnswerResult {
  questionId: number;
  correct: boolean;
}

const NewsFlashGame: React.FC<NewsFlashGameProps> = ({ ageGroup, onClose }) => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [questions, setQuestions] = useState<NewsFlashQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_SECONDS);
  const [score, setScore] = useState(0);
  const [results, setResults] = useState<AnswerResult[]>([]);

  // Attempt tracking: today's official record (if any) and whether this run is practice
  const [previousSession, setPreviousSession] = useState<NewsFlashSessionSummary | null>(null);
  const [isPractice, setIsPractice] = useState(false);
  const [recorded, setRecorded] = useState<boolean | null>(null);

  // Current-question UI state
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [correctIndex, setCorrectIndex] = useState<number | null>(null);
  const [locked, setLocked] = useState(false);

  const questionStartRef = useRef<number>(0);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phaseRef = useRef<Phase>('loading');
  phaseRef.current = phase;
  const finishSentRef = useRef(false);

  // --- Theme by age group ---
  const theme = {
    '6-9': { grad: 'from-yellow-400 via-orange-400 to-pink-400', accent: 'bg-orange-500', ring: 'text-orange-500' },
    '10-13': { grad: 'from-blue-500 via-cyan-500 to-indigo-500', accent: 'bg-blue-600', ring: 'text-blue-500' },
    '14+': { grad: 'from-indigo-600 via-purple-600 to-blue-700', accent: 'bg-indigo-600', ring: 'text-indigo-500' },
  }[ageGroup];

  const gameName = GAME_NAMES[ageGroup];
  const optionLabels = ['A', 'B', 'C', 'D'];

  // Countdown ring geometry
  const RING_R = 16;
  const RING_C = 2 * Math.PI * RING_R;
  const ringOffset = RING_C * (1 - timeLeft / GAME_SECONDS);

  // --- Load today's set + attempt status (on open) ---
  const loadToday = useCallback(async () => {
    setPhase('loading');
    try {
      const data = await newsflashAPI.getToday(ageGroup);
      if (!data.questions || data.questions.length === 0) {
        setPhase('empty');
        return;
      }
      setQuestions(data.questions);
      setPreviousSession(data.previous_session);
      setPhase('intro');
    } catch (err) {
      console.error('NewsFlash load failed:', err);
      setPhase('error');
    }
  }, [ageGroup]);

  useEffect(() => {
    loadToday();
  }, [loadToday]);

  // --- Begin a play-through (fresh or practice) ---
  const beginPlaying = useCallback(async () => {
    // If today's official attempt already exists, this run is practice.
    let practice = previousSession !== null;

    // Lock-on-start: for the first (official) attempt, claim it now so closing
    // mid-quiz can't be turned into a fresh counted retry.
    if (!practice) {
      try {
        const res = await newsflashAPI.start();
        practice = !res.is_official;
        if (!res.is_official) setPreviousSession(res.session);
      } catch (err) {
        console.error('NewsFlash start failed:', err);
        // Proceed as official; finish will record it as a fallback.
        practice = false;
      }
    }

    setIsPractice(practice);
    setRecorded(null);
    finishSentRef.current = false;
    setCurrentIndex(0);
    setScore(0);
    setResults([]);
    setSelectedIndex(null);
    setCorrectIndex(null);
    setLocked(false);
    setTimeLeft(GAME_SECONDS);
    questionStartRef.current = Date.now();
    setPhase('playing');
  }, [previousSession]);

  // --- Countdown timer ---
  useEffect(() => {
    if (phase !== 'playing') return;
    const interval = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(interval);
          setPhase('finished');
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  // --- Cleanup pending advance timer on unmount ---
  useEffect(() => {
    return () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    };
  }, []);

  const finishOrAdvance = useCallback((nextIndex: number) => {
    if (nextIndex >= questions.length) {
      setPhase('finished');
      return;
    }
    setCurrentIndex(nextIndex);
    setSelectedIndex(null);
    setCorrectIndex(null);
    setLocked(false);
    questionStartRef.current = Date.now();
  }, [questions.length]);

  const handleSelect = useCallback(async (optionIdx: number) => {
    if (locked || phaseRef.current !== 'playing') return;
    const question = questions[currentIndex];
    if (!question) return;

    setLocked(true);
    setSelectedIndex(optionIdx);
    const secondsTaken = (Date.now() - questionStartRef.current) / 1000;

    try {
      const res = await newsflashAPI.submitAnswer(question.id, optionIdx);
      setCorrectIndex(res.correct_index);
      if (res.correct) {
        setScore((s) => s + POINTS_PER_CORRECT + speedBonus(secondsTaken));
      }
      setResults((r) => [...r, { questionId: question.id, correct: res.correct }]);
    } catch (err) {
      console.error('Answer submit failed:', err);
      // On network error, count as attempted-wrong and move on rather than blocking the game.
      setResults((r) => [...r, { questionId: question.id, correct: false }]);
    }

    advanceTimerRef.current = setTimeout(() => {
      if (phaseRef.current === 'playing') finishOrAdvance(currentIndex + 1);
    }, FEEDBACK_MS);
  }, [locked, questions, currentIndex, finishOrAdvance]);

  // --- Derived end-screen stats ---
  const total = questions.length;
  const answered = results.length;
  const correctCount = results.filter((r) => r.correct).length;
  const wrongCount = answered - correctCount;
  const skippedCount = Math.max(0, total - answered);

  // --- Record the result once when the game ends ---
  useEffect(() => {
    if (phase !== 'finished' || finishSentRef.current) return;
    finishSentRef.current = true;
    // Practice runs never affect the official record.
    if (isPractice) {
      setRecorded(false);
      return;
    }
    newsflashAPI
      .finish({
        score,
        correct_count: correctCount,
        wrong_count: wrongCount,
        skipped_count: skippedCount,
        total_questions: total,
      })
      .then((res) => setRecorded(res.recorded))
      .catch((err) => {
        console.error('NewsFlash finish failed:', err);
        setRecorded(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Close handler: if the official attempt is abandoned mid-quiz via the ✕,
  // finalize it with the partial score so it's locked (no fresh counted retry).
  const handleClose = useCallback(() => {
    if (phase === 'playing' && !isPractice && !finishSentRef.current) {
      finishSentRef.current = true;
      newsflashAPI
        .finish({
          score,
          correct_count: correctCount,
          wrong_count: wrongCount,
          skipped_count: skippedCount,
          total_questions: total,
        })
        .catch((err) => console.error('NewsFlash partial finish failed:', err));
    }
    onClose();
  }, [phase, isPractice, score, correctCount, wrongCount, skippedCount, total, onClose]);

  const getVerdict = () => {
    const ratio = total > 0 ? correctCount / total : 0;
    if (ratio >= 0.9) return { title: 'News Champion', emoji: '🏆' };
    if (ratio >= 0.7) return { title: 'Sharp Reader', emoji: '⚡' };
    if (ratio >= 0.4) return { title: 'Getting There', emoji: '📚' };
    return { title: 'News Rookie', emoji: '🌱' };
  };

  const optionClasses = (idx: number) => {
    const base = 'w-full text-left rounded-2xl border-2 px-4 py-4 flex items-center gap-3 transition-all duration-150';
    if (correctIndex === null) {
      // not yet answered
      return `${base} bg-white border-gray-200 hover:border-gray-400 active:scale-[0.98] ${locked ? 'opacity-60' : ''}`;
    }
    if (idx === correctIndex) return `${base} bg-green-50 border-green-500`;
    if (idx === selectedIndex && idx !== correctIndex) return `${base} bg-red-50 border-red-500`;
    return `${base} bg-white border-gray-200 opacity-60`;
  };

  const question = questions[currentIndex];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
      >
        {/* Header bar */}
        <div className={`bg-gradient-to-r ${theme.grad} px-5 py-4 flex items-center justify-between text-white`}>
          <div className="flex items-center gap-2 font-extrabold text-lg tracking-tight">
            <BoltIcon className="h-6 w-6" />
            {gameName}
          </div>
          {phase === 'playing' && (
            <div className="relative h-11 w-11" aria-label={`${timeLeft} seconds left`}>
              <svg className="h-11 w-11 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r={RING_R} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
                <circle
                  cx="18" cy="18" r={RING_R} fill="none"
                  stroke={timeLeft <= 10 ? '#FDE047' : '#FFFFFF'}
                  strokeWidth="3" strokeLinecap="round"
                  strokeDasharray={RING_C}
                  strokeDashoffset={ringOffset}
                  style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.3s' }}
                />
              </svg>
              <span className={`absolute inset-0 grid place-items-center text-sm font-extrabold ${timeLeft <= 10 ? 'text-yellow-200 animate-pulse' : 'text-white'}`}>
                {timeLeft}
              </span>
            </div>
          )}
          <button onClick={handleClose} className="p-1.5 rounded-full hover:bg-white/20 transition-colors" aria-label="Close">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            {/* INTRO */}
            {phase === 'intro' && (
              <motion.div key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="p-8 text-center">
                {previousSession ? (
                  // Already played the official round today
                  <>
                    <div className="text-6xl mb-3">🏆</div>
                    <h2 className="text-2xl font-extrabold text-gray-900 mb-1">You already played today!</h2>
                    <p className="text-gray-600 mb-5">Today's score is locked in:</p>
                    <div className={`inline-flex flex-col items-center bg-gradient-to-r ${theme.grad} text-white rounded-3xl px-10 py-4 shadow-lg mb-6`}>
                      <span className="text-sm font-medium opacity-90">Your score</span>
                      <span className="text-4xl font-extrabold leading-none">{previousSession.score}</span>
                      <span className="text-xs opacity-90 mt-1">{previousSession.correct_count}/{previousSession.total_questions} correct</span>
                    </div>
                    <div className="bg-gray-50 rounded-2xl p-3 text-sm text-gray-600 mb-6">
                      Play again to practice — your official score won't change.
                    </div>
                    <button
                      onClick={beginPlaying}
                      className={`w-full ${theme.accent} text-white font-bold text-lg py-4 rounded-2xl shadow-lg hover:opacity-90 active:scale-[0.98] transition-all`}
                    >
                      Practice Again
                    </button>
                  </>
                ) : (
                  // Fresh, first attempt of the day
                  <>
                    <div className="text-6xl mb-4">⚡</div>
                    <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Race the clock!</h2>
                    <p className="text-gray-600 mb-6">
                      Answer as many questions as you can in <b>99 seconds</b>. Every question comes from today's Knoozy news.
                    </p>
                    <div className="bg-gray-50 rounded-2xl p-4 text-sm text-gray-600 mb-4 space-y-1 text-left">
                      <p>• <b>10 points</b> for each correct answer</p>
                      <p>• <b>+2 speed bonus</b> for answering fast</p>
                      <p>• Wrong answers cost nothing — just keep going!</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-sm font-semibold text-amber-700 mb-6">
                      📖 Read today's stories first — the answers are hiding in the news!
                    </div>
                    <button
                      onClick={beginPlaying}
                      className={`w-full ${theme.accent} text-white font-bold text-lg py-4 rounded-2xl shadow-lg hover:opacity-90 active:scale-[0.98] transition-all`}
                    >
                      Start {gameName}
                    </button>
                  </>
                )}
              </motion.div>
            )}

            {/* LOADING */}
            {phase === 'loading' && (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-12 text-center">
                <div className={`mx-auto h-12 w-12 rounded-full border-4 border-gray-200 border-t-current animate-spin ${theme.ring}`} />
                <p className="text-gray-500 mt-4">Loading today's questions…</p>
              </motion.div>
            )}

            {/* EMPTY / ERROR */}
            {(phase === 'empty' || phase === 'error') && (
              <motion.div key="msg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-10 text-center">
                <div className="text-5xl mb-4">{phase === 'empty' ? '📭' : '⚠️'}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {phase === 'empty' ? `Today's ${gameName} isn't ready yet` : 'Something went wrong'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {phase === 'empty'
                    ? 'Check back a little later — quizzes are generated after the day’s news is published.'
                    : 'We could not load the quiz. Please try again.'}
                </p>
                <button onClick={phase === 'error' ? loadToday : onClose} className={`px-6 py-3 ${theme.accent} text-white font-semibold rounded-2xl`}>
                  {phase === 'error' ? 'Retry' : 'Close'}
                </button>
              </motion.div>
            )}

            {/* PLAYING */}
            {phase === 'playing' && question && (
              <motion.div
                key={`q-${question.id}`}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.2 }}
                className="p-5"
              >
                {/* progress + category */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Question {currentIndex + 1} of {total}
                  </span>
                  <span className={`text-xs font-bold text-white px-2.5 py-1 rounded-full ${theme.accent}`}>
                    {question.category.replace('_', ' ')}
                  </span>
                </div>
                {/* progress bar */}
                <div className="h-1.5 w-full bg-gray-100 rounded-full mb-5 overflow-hidden">
                  <div className={`h-full ${theme.accent} transition-all duration-300`} style={{ width: `${((currentIndex) / total) * 100}%` }} />
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-5 leading-snug">{question.question_text}</h3>

                <div className="space-y-3">
                  {question.options.map((opt, idx) => (
                    <button key={idx} onClick={() => handleSelect(idx)} disabled={locked} className={optionClasses(idx)}>
                      <span className={`flex-shrink-0 h-7 w-7 rounded-full grid place-items-center text-sm font-bold
                        ${correctIndex === idx ? 'bg-green-500 text-white'
                          : selectedIndex === idx && correctIndex !== null ? 'bg-red-500 text-white'
                          : 'bg-gray-100 text-gray-600'}`}>
                        {optionLabels[idx]}
                      </span>
                      <span className="font-medium text-gray-800">{opt}</span>
                    </button>
                  ))}
                </div>

                <div className="mt-4 text-center text-sm font-semibold text-gray-400">Score: {score}</div>
              </motion.div>
            )}

            {/* FINISHED */}
            {phase === 'finished' && (
              <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="p-8 text-center">
                <div className="text-6xl mb-2">{getVerdict().emoji}</div>
                <h2 className="text-2xl font-extrabold text-gray-900">{getVerdict().title}</h2>
                <div className={`mt-4 mb-6 inline-flex flex-col items-center bg-gradient-to-r ${theme.grad} text-white rounded-3xl px-10 py-5 shadow-lg`}>
                  <span className="text-sm font-medium opacity-90">Final Score</span>
                  <span className="text-5xl font-extrabold leading-none">{score}</span>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-green-50 rounded-2xl py-3">
                    <div className="text-2xl font-extrabold text-green-600">{correctCount}</div>
                    <div className="text-xs font-semibold text-green-700 uppercase">Correct</div>
                  </div>
                  <div className="bg-red-50 rounded-2xl py-3">
                    <div className="text-2xl font-extrabold text-red-500">{wrongCount}</div>
                    <div className="text-xs font-semibold text-red-600 uppercase">Wrong</div>
                  </div>
                  <div className="bg-gray-100 rounded-2xl py-3">
                    <div className="text-2xl font-extrabold text-gray-500">{skippedCount}</div>
                    <div className="text-xs font-semibold text-gray-600 uppercase">Skipped</div>
                  </div>
                </div>

                {isPractice ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3 text-sm font-medium text-gray-600 mb-6">
                    🔁 Practice round — your official score for today stays unchanged.
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-6">
                    {recorded === true && <span className="block font-semibold text-gray-700 mb-1">✅ Score saved as today's result!</span>}
                    📰 Read all of today's Knoozy stories — readers always score higher!
                  </p>
                )}

                <button onClick={onClose} className={`w-full ${theme.accent} text-white font-bold py-4 rounded-2xl shadow-lg hover:opacity-90 active:scale-[0.98] transition-all`}>
                  Done
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default NewsFlashGame;
